// src/pages/TermsOfUse.jsx
import React, { useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  UnorderedList,
  ListItem,
  OrderedList,
  Divider,
  useColorModeValue
} from '@chakra-ui/react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const TermsOfUse = () => {
  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Header />
      <Box pt="100px" pb="60px" minH="100vh" bg={useColorModeValue('gray.50', 'gray.900')}>
        <Container maxW="1000px">
          <VStack spacing={8} align="stretch">
            <Heading 
              as="h1" 
              fontSize={{ base: '2xl', md: '3xl', lg: '4xl' }}
              textAlign="center"
              bgGradient="linear(to-r, brand.primary, brand.secondary)"
              bgClip="text"
              mb={8}
            >
              Termos de Uso
            </Heading>

            <Box 
              bg="white" 
              p={8} 
              borderRadius="lg" 
              boxShadow="md"
            >
              <Text color="gray.700" mb={4}>
                Última atualização: 07 de Maio de 2025
              </Text>

              <Text color="gray.700" mb={6}>
                Bem-vindo à Draw Your Goods! Estes Termos de Uso estabelecem as condições para utilização de nossa plataforma. Ao acessar ou usar nosso site, você concorda com estes termos. Por favor, leia-os com atenção.
              </Text>

              <Divider my={6} />

              <VStack spacing={8} align="stretch">
                <Box>
                  <Heading as="h2" size="md" color="brand.primary" mb={4}>
                    1. Aceitação dos Termos
                  </Heading>

                  <Text color="gray.700">
                    Ao acessar ou utilizar a plataforma Draw Your Goods, você concorda em ficar vinculado a estes Termos de Uso, à nossa Política de Privacidade e a quaisquer termos adicionais que possam se aplicar a serviços específicos. Se você não concordar com qualquer parte destes termos, solicitamos que não utilize nossos serviços.
                  </Text>
                </Box>

                <Box>
                  <Heading as="h2" size="md" color="brand.primary" mb={4}>
                    2. Descrição do Serviço
                  </Heading>

                  <Text color="gray.700" mb={4}>
                    A Draw Your Goods oferece um serviço online que permite aos usuários transformar suas fotos em desenhos para colorir através de nossa tecnologia de processamento de imagens. Nossos serviços incluem:
                  </Text>

                  <UnorderedList spacing={2} pl={4} color="gray.700">
                    <ListItem>Transformação de fotos em desenhos para colorir</ListItem>
                    <ListItem>Armazenamento dos desenhos em uma galeria pessoal</ListItem>
                    <ListItem>Download dos desenhos em formatos de alta resolução</ListItem>
                    <ListItem>Sistema de créditos para utilização dos serviços</ListItem>
                  </UnorderedList>
                </Box>

                <Box>
                  <Heading as="h2" size="md" color="brand.primary" mb={4}>
                    3. Conta de Usuário
                  </Heading>

                  <OrderedList spacing={4} pl={4} color="gray.700">
                    <ListItem>
                      <Text fontWeight="medium">Criação de Conta:</Text> Para utilizar nossos serviços, você precisa criar uma conta fornecendo informações precisas e completas.
                    </ListItem>
                    <ListItem>
                      <Text fontWeight="medium">Responsabilidade:</Text> Você é responsável por manter a confidencialidade de sua senha e por todas as atividades que ocorrem em sua conta.
                    </ListItem>
                    <ListItem>
                      <Text fontWeight="medium">Segurança:</Text> Você deve notificar-nos imediatamente sobre qualquer uso não autorizado de sua conta ou qualquer outra violação de segurança.
                    </ListItem>
                    <ListItem>
                      <Text fontWeight="medium">Idade Mínima:</Text> Para criar uma conta e utilizar nossos serviços, você deve ter pelo menos 18 anos ou ter a supervisão e consentimento de um responsável legal.
                    </ListItem>
                  </OrderedList>
                </Box>

                <Box>
                  <Heading as="h2" size="md" color="brand.primary" mb={4}>
                    4. Sistema de Créditos
                  </Heading>

                  <OrderedList spacing={4} pl={4} color="gray.700">
                    <ListItem>
                      <Text fontWeight="medium">Aquisição de Créditos:</Text> Os créditos são adquiridos através da compra de pacotes disponíveis em nossa plataforma.
                    </ListItem>
                    <ListItem>
                      <Text fontWeight="medium">Utilização:</Text> Cada crédito permite a transformação de uma foto em um desenho para colorir.
                    </ListItem>
                    <ListItem>
                      <Text fontWeight="medium">Validade:</Text> Os créditos não expiram e permanecem disponíveis em sua conta por tempo indeterminado.
                    </ListItem>
                    <ListItem>
                      <Text fontWeight="medium">Reembolso:</Text> Os créditos adquiridos não são reembolsáveis, exceto nos casos previstos pela legislação aplicável.
                    </ListItem>
                  </OrderedList>
                </Box>

                <Box>
                  <Heading as="h2" size="md" color="brand.primary" mb={4}>
                    5. Conteúdo do Usuário
                  </Heading>

                  <OrderedList spacing={4} pl={4} color="gray.700">
                    <ListItem>
                      <Text fontWeight="medium">Propriedade:</Text> Você mantém todos os direitos sobre as fotos que faz upload para nossa plataforma.
                    </ListItem>
                    <ListItem>
                      <Text fontWeight="medium">Licença Limitada:</Text> Ao fazer upload de conteúdo, você nos concede uma licença limitada para processar, armazenar e exibir suas imagens com o propósito de fornecer e melhorar nossos serviços.
                    </ListItem>
                    <ListItem>
                      <Text fontWeight="medium">Conteúdo Proibido:</Text> Você não deve fazer upload de conteúdo que:
                      <UnorderedList mt={2} ml={4}>
                        <ListItem>Viole direitos autorais, marcas registradas ou outros direitos de propriedade intelectual</ListItem>
                        <ListItem>Contenha material ilegal, ofensivo, difamatório ou inadequado</ListItem>
                        <ListItem>Inclua imagens de terceiros sem autorização apropriada</ListItem>
                        <ListItem>Contenha malware, vírus ou outro código prejudicial</ListItem>
                      </UnorderedList>
                    </ListItem>
                  </OrderedList>
                </Box>

                <Box>
                  <Heading as="h2" size="md" color="brand.primary" mb={4}>
                    6. Propriedade Intelectual
                  </Heading>

                  <Text color="gray.700" mb={4}>
                    A marca Draw Your Goods, logotipos, tecnologia de processamento de imagens, design do site e outros elementos de nossa plataforma são protegidos por direitos autorais, marcas registradas e outras leis de propriedade intelectual. Estes elementos não podem ser copiados, imitados ou utilizados, no todo ou em parte, sem nossa permissão prévia por escrito.
                  </Text>
                </Box>

                <Box>
                  <Heading as="h2" size="md" color="brand.primary" mb={4}>
                    7. Limitação de Responsabilidade
                  </Heading>

                  <OrderedList spacing={4} pl={4} color="gray.700">
                    <ListItem>
                      <Text fontWeight="medium">Funcionamento do Serviço:</Text> Embora nos esforcemos para manter nossa plataforma funcionando sem interrupções, não garantimos que os serviços estarão sempre disponíveis, seguros ou livres de erros.
                    </ListItem>
                    <ListItem>
                      <Text fontWeight="medium">Responsabilidade por Danos:</Text> Em nenhuma circunstância a Draw Your Goods, seus diretores, funcionários ou agentes serão responsáveis por quaisquer danos indiretos, incidentais, especiais, consequenciais ou punitivos decorrentes ou relacionados ao uso de nossos serviços.
                    </ListItem>
                    <ListItem>
                      <Text fontWeight="medium">Qualidade das Transformações:</Text> Embora nos esforcemos para fornecer transformações de alta qualidade, o resultado final pode variar dependendo da qualidade e características da imagem original. Não garantimos resultados específicos para cada transformação.
                    </ListItem>
                  </OrderedList>
                </Box>

                <Box>
                  <Heading as="h2" size="md" color="brand.primary" mb={4}>
                    8. Alterações nos Termos
                  </Heading>

                  <Text color="gray.700">
                    Reservamo-nos o direito de modificar estes Termos de Uso a qualquer momento, a nosso critério. Quaisquer alterações entrarão em vigor imediatamente após a publicação dos termos revisados. Notificaremos você sobre alterações significativas através de um aviso em nosso site ou por e-mail. O uso contínuo de nossos serviços após tais alterações constitui aceitação dos novos termos.
                  </Text>
                </Box>

                <Box>
                  <Heading as="h2" size="md" color="brand.primary" mb={4}>
                    9. Rescisão
                  </Heading>

                  <Text color="gray.700" mb={4}>
                    Reservamo-nos o direito de suspender ou encerrar sua conta e acesso aos nossos serviços, a nosso critério, sem aviso prévio, por violação destes Termos de Uso ou por qualquer outro motivo que julgarmos apropriado.
                  </Text>

                  <Text color="gray.700">
                    Se você desejar encerrar sua conta, pode fazê-lo a qualquer momento entrando em contato conosco através do e-mail: drawgoodscontato@gmail.com
                  </Text>
                </Box>

                <Box>
                  <Heading as="h2" size="md" color="brand.primary" mb={4}>
                    10. Lei Aplicável
                  </Heading>

                  <Text color="gray.700">
                    Estes Termos de Uso serão regidos e interpretados de acordo com as leis do Brasil, independentemente de conflitos de disposições legais. Qualquer disputa decorrente ou relacionada a estes termos estará sujeita à jurisdição exclusiva dos tribunais localizados no Brasil.
                  </Text>
                </Box>

                <Box>
                  <Heading as="h2" size="md" color="brand.primary" mb={4}>
                    11. Contato
                  </Heading>

                  <Text color="gray.700">
                    Se você tiver dúvidas ou preocupações sobre estes Termos de Uso, entre em contato conosco através do e-mail: drawgoodscontato@gmail.com
                  </Text>
                </Box>
              </VStack>
            </Box>
          </VStack>

          {/* SEO - Schema.org markup for Google */}
          <script type="application/ld+json">{`
            {
              "@context": "https://schema.org",
              "@type": "WebPage",
              "name": "Termos de Uso - Draw Your Goods",
              "description": "Nossos termos de uso estabelecem as condições para utilização da plataforma Draw Your Goods.",
              "publisher": {
                "@type": "Organization",
                "name": "Draw Your Goods",
                "logo": "https://www.drawyourgoods.com.br/logo.png"
              },
              "datePublished": "2025-05-07",
              "dateModified": "2025-05-07"
            }
          `}</script>
        </Container>
      </Box>
      <Footer />
    </>
  );
};

export default TermsOfUse;